import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Dialog, DialogActions, DialogTitle, IconButton, LinearProgress, Stack,   ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { Close, DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { emailService } from '../../services/emailService';
import { useDebounce } from '../../hooks/useDebounce';
import type { EmailPreview, EmailSettings, EmailTypeSetting } from '../../types/email';
import EmailCopyEditor from './EmailCopyEditor';
import { copyErrors, currentCopy, sameCopy } from './emailCopy';

interface Props {
  type: EmailTypeSetting;
  writable: boolean;
  onClose: () => void;
  onSaved: (next: EmailSettings) => void;
}

/** The email's own page colour, so the frame and its surroundings read as one surface. */
const MAIL_PAGE = '#f1f5f9';
const PHONE_WIDTH = 390;

/**
 * One email exactly as a family receives it, in a mail-client frame, with
 * its wording editable beside it and previewed as it's typed. The frame
 * grows to the email's height, so only the pane scrolls.
 */
const EmailEditDialog: React.FC<Props> = ({ type, writable, onClose, onSaved }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const saved = useMemo(() => currentCopy(type), [type]);
  const [draft, setDraft] = useState(saved);
  const typed = useDebounce(draft, 400);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const frame = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState(640);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    emailService.preview(type.key, typed)
      .then(p => { if (!stale) { setPreview(p); setProblem(null); } })
      .catch(err => { if (!stale) setProblem(err?.message || 'Couldn’t load the preview'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [type.key, typed]);

  // Same-origin (never scripts) so the frame can be measured.
  const fit = useCallback(() => {
    // The body, not the document: the document is never shorter than the frame, so it couldn't shrink.
    const body = frame.current?.contentDocument?.body;
    if (body) setFrameHeight(body.scrollHeight);
  }, []);
  // Re-measure once the width change has settled.
  useEffect(() => {
    const t = setTimeout(fit, 220);
    return () => clearTimeout(t);
  }, [device, fit]);

  const save = async () => {
    setSaving(true);
    try {
      onSaved(await emailService.updateSettings({ types: [{ type: type.key, copy: draft }] }));
      enqueueSnackbar(`${type.label} email saved`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t save the email', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const changed = !sameCopy(draft, saved);
  const invalid = Object.keys(copyErrors(type, draft)).length > 0;

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      <DialogTitle component="div" sx={{ py: 2, pr: 7, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>{type.label}</Typography>
        <Typography variant="body2" color="text.secondary">{type.description}</Typography>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ position: 'absolute', right: 14, top: 14 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: writable ? 'minmax(0, 1fr) 380px' : '1fr' },
        height: { md: 'min(720px, calc(100vh - 220px))' },
        minHeight: 0,
      }}>
        {/* ── The inbox view ─────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ mb: 1 }}>{preview?.subject ?? ' '}</Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 36, height: 36, fontSize: 15, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
                    {preview?.from.charAt(0).toUpperCase() ?? ''}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      <b>{preview?.from}</b>
                      <Box component="span" sx={{ color: 'text.secondary' }}>{preview ? ` <${preview.fromAddress}>` : ''}</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      to {preview?.to}{preview?.replyTo ? ` · replies go to ${preview.replyTo}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <ToggleButtonGroup
                size="small" exclusive value={device} onChange={(_, v) => v && setDevice(v)}
                sx={{ flexShrink: 0, '& .MuiToggleButton-root': { px: 1.25 } }}
              >
                <ToggleButton value="desktop" aria-label="Desktop width"><DesktopWindowsOutlined fontSize="small" /></ToggleButton>
                <ToggleButton value="phone" aria-label="Phone width"><PhoneIphoneOutlined fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
          <Box sx={{ position: 'relative', flex: 1, minHeight: { xs: 480, md: 0 }, overflow: 'auto', bgcolor: MAIL_PAGE }}>
            {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />}
            {problem ? (
              <Alert severity="error" sx={{ m: 3 }}>{problem}</Alert>
            ) : preview && (
              <Box
                component="iframe"
                ref={frame}
                title={`${type.label} preview`}
                sandbox="allow-same-origin"
                srcDoc={preview.html}
                onLoad={fit}
                sx={{
                  display: 'block', border: 0, mx: 'auto', bgcolor: MAIL_PAGE,
                  width: device === 'phone' ? PHONE_WIDTH : '100%',
                  height: frameHeight,
                  transition: 'width 200ms ease',
                }}
              />
            )}
          </Box>
        </Box>

        {/* ── The wording ─────────────────────────────────────────────── */}
        {writable && (
          <Box sx={{
            p: 3, overflow: 'auto',
            borderLeft: { md: `1px solid ${theme.palette.divider}` },
            borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
          }}>
            <EmailCopyEditor type={type} value={draft} onChange={setDraft} disabled={saving} />
          </Box>
        )}
      </Box>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>{writable && changed ? 'Cancel' : 'Close'}</Button>
        {writable && (
          <Button variant="contained" disableElevation onClick={save} disabled={!changed || invalid || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailEditDialog;
